import { useState, useEffect } from 'react';
import { X, AlertCircle, Loader, Server, Globe } from 'lucide-react';
import { modpackNotesApiService } from '../services/modpackNotesApi';
import { configService } from '../services/usersApi';
import { clusterManagementService } from '../services/clusterManagementService';
import { ServerConfig } from '../types/player';
import { motion, AnimatePresence } from 'framer-motion';
import MarkdownEditor from './MarkdownEditor';
import { JsonMetadataBuilder } from './JsonMetadataBuilder';
import { cn } from '../utils/cn';

interface CreateModpackNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preselectedCluster?: string;
  preselectedFqdn?: string;
}

type AssociationType = 'cluster' | 'server';

export const CreateModpackNoteModal = ({
  isOpen,
  onClose,
  onSuccess,
  preselectedCluster,
  preselectedFqdn,
}: CreateModpackNoteModalProps) => {
  const [associationType, setAssociationType] = useState<AssociationType>(
    preselectedFqdn ? 'server' : 'cluster'
  );
  const [clusterId, setClusterId] = useState(preselectedCluster || '');
  const [fqdn, setFqdn] = useState(preselectedFqdn || '');
  const [content, setContent] = useState('');
  const [metadata, setMetadata] = useState('');
  const [showMetadata, setShowMetadata] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [configs, setConfigs] = useState<ServerConfig[]>([]);
  const [clusterFqdnMap, setClusterFqdnMap] = useState<Record<string, string[]>>({});
  const [loadingConfigs, setLoadingConfigs] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoadingConfigs(true);
      try {
        const [serverConfigs, fqdnMapping] = await Promise.all([
          configService.getConfigs(),
          clusterManagementService.getClusterFqdnMapping(),
        ]);
        setConfigs(serverConfigs);
        setClusterFqdnMap(fqdnMapping);
      } catch (err) {
        console.error('Failed to load cluster data:', err);
      } finally {
        setLoadingConfigs(false);
      }
    };
    if (isOpen) loadData();
  }, [isOpen]);

  const uniqueClusters = configs
    .reduce((acc, current) => {
      if (current.cluster_name && !acc.find((item) => item.cluster_name === current.cluster_name)) {
        acc.push(current);
      }
      return acc;
    }, [] as ServerConfig[])
    .sort((a, b) => a.cluster_name.localeCompare(b.cluster_name));

  const availableFqdns = clusterId ? (clusterFqdnMap[clusterId] || []) : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const sendClusterId = clusterId || undefined;
    const sendFqdn = associationType === 'server' ? (fqdn || undefined) : undefined;

    if (!sendClusterId) {
      setError('A cluster must be selected.');
      return;
    }
    if (associationType === 'server' && !sendFqdn) {
      setError('A server FQDN must be selected.');
      return;
    }
    if (!content.trim()) {
      setError('Content cannot be empty.');
      return;
    }

    setIsSubmitting(true);
    try {
      await modpackNotesApiService.createNote({
        content,
        cluster_id: sendClusterId || undefined,
        fqdn: sendFqdn || undefined,
        metadata: metadata.trim() || undefined,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create modpack note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setContent('');
    setMetadata('');
    setError(null);
    setShowMetadata(false);
    if (!preselectedCluster) setClusterId('');
    if (!preselectedFqdn) setFqdn('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="modal-panel max-w-3xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold tracking-tight text-white">Create Modpack Note</h2>
            <button onClick={handleClose} className="glass-button p-2 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="glass-card p-4 mb-4 border-l-4 border-red-400 bg-red-500/10">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-300">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Association Type Selector */}
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Note Scope <span className="text-red-400">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { type: 'cluster' as const, label: 'Cluster', icon: Server, desc: 'Applies to entire cluster' },
                  { type: 'server' as const, label: 'Server', icon: Globe, desc: 'Cluster + specific server' },
                ]).map(({ type, label, icon: Icon, desc }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setAssociationType(type)}
                    className={cn(
                      "p-3 rounded-lg border text-left transition-all touch-manipulation",
                      associationType === type
                        ? "border-accent-400 bg-accent-500/15 text-accent-300"
                        : "border-glass-200/20 bg-glass-100/5 text-glass-400 hover:border-glass-200/40"
                    )}
                  >
                    <Icon className="w-4 h-4 mb-1" />
                    <div className="text-sm font-semibold">{label}</div>
                    <div className="text-xs opacity-70">{desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Cluster Selector — always shown since both scopes need a cluster */}
            <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Cluster <span className="text-red-400">*</span>
                </label>
                {loadingConfigs ? (
                  <div className="flex items-center gap-2 text-glass-400 text-sm py-2">
                    <Loader className="w-4 h-4 animate-spin" />
                    Loading clusters...
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-1">
                    {uniqueClusters.map((config) => (
                      <button
                        key={config.cluster_name}
                        type="button"
                        onClick={() => {
                          setClusterId(config.cluster_name);
                          // Reset FQDN if cluster changes
                          if (fqdn && !clusterFqdnMap[config.cluster_name]?.includes(fqdn)) {
                            setFqdn('');
                          }
                        }}
                        className={cn(
                          "p-2 rounded-lg transition-all border-2 touch-manipulation",
                          clusterId === config.cluster_name
                            ? "bg-accent-500/30 border-accent-400"
                            : "bg-glass-100/20 border-transparent hover:border-accent-500/50"
                        )}
                      >
                        <img
                          src={config.thumbnail_url}
                          alt={config.cluster_name}
                          className="w-10 h-10 object-cover rounded-md mx-auto"
                        />
                        <p className="text-center text-[10px] mt-1 text-white truncate">
                          {config.cluster_name}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

            {/* FQDN Selector — only shown for 'server' scope */}
            {associationType === 'server' && (
              <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Server FQDN <span className="text-red-400">*</span>
                </label>
                {(clusterId && availableFqdns.length > 0) ? (
                  <select
                    value={fqdn}
                    onChange={(e) => setFqdn(e.target.value)}
                    className="glass-input w-full"
                  >
                    <option value="">Select a server...</option>
                    {availableFqdns.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={fqdn}
                    onChange={(e) => setFqdn(e.target.value)}
                    className="glass-input w-full"
                    placeholder={clusterId ? "Enter FQDN manually..." : "Select a cluster first, or enter FQDN manually..."}
                  />
                )}
              </div>
            )}

            {/* Content */}
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Content <span className="text-red-400">*</span>
              </label>
              <MarkdownEditor
                value={content}
                onChange={setContent}
                height={250}
                placeholder="Describe the modpack change, configuration update, known issue..."
              />
              <p className="text-xs text-glass-400 mt-1">
                Supports markdown — use headings, lists, code blocks, etc.
              </p>
            </div>

            {/* Metadata (collapsible JSON builder) */}
            <div>
              <button
                type="button"
                onClick={() => setShowMetadata(!showMetadata)}
                className="text-sm text-glass-400 hover:text-glass-200 transition-colors flex items-center gap-1"
              >
                <span>{showMetadata ? '▾' : '▸'}</span>
                <span>Metadata (optional)</span>
              </button>
              {showMetadata && (
                <div className="mt-2">
                  <JsonMetadataBuilder
                    value={metadata}
                    onChange={setMetadata}
                  />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="glass-button-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="glass-button"
                disabled={isSubmitting || !content.trim()}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Note'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
